import { extendTheme } from '@chakra-ui/react';

// Storefront design tokens. Values and their sources: design-system.md §2.
// Archivo is variable on width (62–125%) and weight, so one family covers the wide UI
// text and the condensed price/button style (font-stretch: 75%).

const fonts = {
    body: "'Archivo', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    heading: "'Archivo', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    ai: "'Barlow', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    // Chakra's original stack, kept for the admin panel
    system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
};

const WIDE = '112.5%';
const CONDENSED = '75%';

const colors = {
    raano: {
        ink: '#1F2937',
        inkSoft: '#374151',
        black: '#000000',
        white: '#FFFFFF',
        onDark: '#F9FAFB',
        muted: 'rgba(0, 0, 0, 0.6)',
        border: '#E6E6E6',
        sizeBorder: '#E1E0EC',
        qtyBorder: '#E2D8F3',
        band: '#F3F4F6',
        imageBg: '#E3E7ED',
        surface: '#F5F5F5',
        chip: '#EFEFEF',
        disabledBorder: '#E0E0E0',
        disabledText: '#D0D0D0',
        buyNowDisabled: '#666666',
        accent: '#6446BE',
        stockIn: '#3ED660',
        stockLow: '#EE9441',
        stockOut: '#C8C8C8',
        error: '#8B0000',
        success: '#006400',
    },
    // AI agent, light and dark (design-system.md §9.2)
    ai: {
        panel: '#FFFFFF', panelDark: '#292929',
        border: '#E0E0E0', borderDark: '#525252',
        text: '#242424',
        bubble: '#F0F0F0', bubbleDark: '#0A0A0A',
        chipBorder: '#D1D1D1', chipBorderDark: '#666666',
    },
};

const theme = extendTheme({
    fonts,
    colors,
    // md = phone → tablet, lg = tablet → desktop
    breakpoints: { sm: '30em', md: '750px', lg: '1025px', xl: '80em', '2xl': '96em' },
    radii: {
        sizeBtn: '10px',
        buyBtn: '8px',
        primaryBtn: '5px',
        stepper: '5px',
        searchPill: '30px',
        drawer: '16px',
        aiPanel: '24px',
        tabbar: '20px',
    },
    shadows: {
        popover: '0 4px 20px rgba(0, 0, 0, 0.15)',
        drawer: '0 5px 30px rgba(0, 0, 0, 0.15)',
        tabbar: '0 -2px 10px rgba(0, 0, 0, 0.10)',
        aiPill: '0 2px 20px rgba(0, 0, 0, 0.25)',
    },
    transition: {
        duration: { micro: '125ms', drawer: '200ms', hover: '250ms', wipe: '400ms', image: '500ms' },
        easing: { easeOutCubic: 'cubic-bezier(0.33, 1, 0.68, 1)' },
    },
    textStyles: {
        // Condensed semibold, used for prices, buy buttons, SORT / FILTER
        condensed: { fontStretch: CONDENSED, fontWeight: 600 },
        price: { fontStretch: CONDENSED, fontWeight: 600, fontSize: { base: '14px', md: '14px' }, lineHeight: '1.25' },
        priceLarge: { fontStretch: CONDENSED, fontWeight: 600, fontSize: { base: '14.7px', md: '18px' }, lineHeight: '1.25', textTransform: 'uppercase' },
        buttonLabel: { fontStretch: CONDENSED, fontWeight: 600, fontSize: { base: '16px', md: '18px' }, lineHeight: '1.5', textTransform: 'uppercase' },
        cardTitle: { fontSize: { base: '12px', md: '14px' }, lineHeight: { base: '18px', md: '21px' }, textTransform: 'capitalize' },
        pdpTitle: { fontSize: { base: '18px', md: '22px' }, lineHeight: '1.25', fontWeight: 500 },
        breadcrumb: { fontSize: { base: '14px', md: '16px' }, lineHeight: '1.8' },
        // Banner CTA labels ("SHOP FOR MEN →")
        bannerCta: { fontWeight: 700, fontSize: { base: '14px', md: '16px' }, letterSpacing: '0.04em', textTransform: 'uppercase' },
        footerHeading: { fontSize: { base: '16px', md: '14px' }, fontWeight: 600 },
    },
    styles: {
        global: {
            body: {
                fontFamily: 'body',
                fontStretch: WIDE,
                color: 'raano.black',
                bg: 'raano.white',
                WebkitFontSmoothing: 'antialiased',
            },
            'h1, h2, h3, h4, h5, h6': { letterSpacing: 0 },
            '@media (prefers-reduced-motion: reduce)': {
                '*, *::before, *::after': { animationDuration: '0.01ms !important', animationIterationCount: '1 !important', transitionDuration: '0.01ms !important' },
            },
        },
    },
    components: {
        // Black track when on (design-system.md §12.2)
        Switch: {
            baseStyle: { track: { _checked: { bg: 'raano.black' } } },
        },
        Button: {
            variants: {
                // White button whose black fill wipes down from the top on hover (design-system.md §8.1)
                wipe: {
                    position: 'relative',
                    overflow: 'hidden',
                    zIndex: 0,
                    bg: 'raano.white',
                    color: 'raano.black',
                    border: '1px solid',
                    borderColor: 'raano.black',
                    borderRadius: 'buyBtn',
                    fontStretch: CONDENSED,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    transition: 'color 0.2s',
                    _after: {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0,
                        height: '0%',
                        bg: 'raano.black',
                        transition: 'height 0.4s ease-in-out',
                        zIndex: -1,
                        pointerEvents: 'none',
                    },
                    _hover: { color: 'raano.white', _after: { height: '100%' }, _disabled: { color: 'raano.disabledText', _after: { height: '0%' } } },
                    _active: { _after: { height: '100%' }, color: 'raano.white' },
                    _disabled: { bg: 'raano.surface', borderColor: 'raano.disabledBorder', color: 'raano.disabledText', opacity: 1, cursor: 'not-allowed' },
                },
                // Solid black: BUY IT NOW, PROCEED TO CHECKOUT, SEE ALL RESULTS
                ink: {
                    bg: 'raano.black',
                    color: 'raano.white',
                    border: '1px solid',
                    borderColor: 'raano.black',
                    borderRadius: 'buyBtn',
                    fontStretch: CONDENSED,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    _hover: { bg: '#1A1A1A', _disabled: { bg: 'raano.buyNowDisabled' } },
                    _active: { bg: '#262626' },
                    _disabled: { bg: 'raano.buyNowDisabled', borderColor: 'raano.buyNowDisabled', opacity: 1, cursor: 'not-allowed' },
                },
                // Purple, only for the login and 404 primary buttons
                accent: {
                    bg: 'raano.accent',
                    color: 'raano.white',
                    borderRadius: 'primaryBtn',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontStretch: CONDENSED,
                    _hover: { bg: '#5537AC', _disabled: { bg: 'raano.accent' } },
                    _active: { bg: '#4A2F98' },
                },
            },
        },
    },
});

export default theme;
